import AsyncStorage from '@react-native-async-storage/async-storage';
import type { UserProfile } from '@/state/userStore';

const AUTH_BOOTSTRAP_CACHE_PREFIX = '@tripflow_auth_bootstrap:';
const LAST_SIGNED_IN_UID_KEY = '@tripflow_last_signed_in_uid';

export type AuthBootstrapSnapshot = {
  uid: string;
  profile: UserProfile;
  hasCompletedOnboarding: boolean;
  isNewUser: boolean;
  hasCompletedTutorial: boolean;
  shouldShowTutorial: boolean;
};

const getKey = (uid: string) => `${AUTH_BOOTSTRAP_CACHE_PREFIX}${uid}`;

export const loadAuthBootstrapSnapshot = async (
  uid: string,
): Promise<AuthBootstrapSnapshot | null> => {
  try {
    const raw = await AsyncStorage.getItem(getKey(uid));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AuthBootstrapSnapshot;
    if (!parsed || parsed.uid !== uid) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const saveAuthBootstrapSnapshot = async (
  snapshot: AuthBootstrapSnapshot,
): Promise<void> => {
  try {
    await AsyncStorage.setItem(getKey(snapshot.uid), JSON.stringify(snapshot));
    await AsyncStorage.setItem(LAST_SIGNED_IN_UID_KEY, snapshot.uid);
  } catch {
    // Best-effort cache only.
  }
};

export const clearAuthBootstrapSnapshot = async (uid: string): Promise<void> => {
  try {
    await AsyncStorage.removeItem(getKey(uid));
    await AsyncStorage.removeItem(LAST_SIGNED_IN_UID_KEY);
  } catch {
    // Best-effort cache only.
  }
};

export const loadLastSignedInUid = async (): Promise<string | null> => {
  try {
    return await AsyncStorage.getItem(LAST_SIGNED_IN_UID_KEY);
  } catch {
    return null;
  }
};

export const loadLastSignedInSnapshot = async (): Promise<AuthBootstrapSnapshot | null> => {
  const uid = await loadLastSignedInUid();
  if (!uid) return null;
  return loadAuthBootstrapSnapshot(uid);
};
